"use client"

import type { UserPreferences } from "@/lib/db/schema"

/**
 * iOS Install Wizard Utilities
 *
 * Manages the state and behavior of the iOS PWA installation wizard.
 * Implements progressive escalation with time-based cooldowns and permanent opt-out.
 */

// localStorage keys for fast client-side checks
const WIZARD_DISMISSED_UNTIL_KEY = "ios-wizard-dismissed-until"
const WIZARD_PERMANENT_OPT_OUT_KEY = "ios-wizard-permanent-opt-out"

/**
 * Get cooldown period in days based on dismissal count
 * Progressive escalation: 3 → 7 → 14 → 30 days
 */
export function getWizardCooldownDays(dismissCount: number): number {
  if (dismissCount === 0) return 0
  if (dismissCount === 1) return 3
  if (dismissCount === 2) return 7
  if (dismissCount === 3) return 14
  return 30 // 4th+ dismissal (soft permanent opt-out)
}

/**
 * Check if wizard should be shown based on user preferences
 */
export function shouldShowIOSWizard(preferences: UserPreferences | null): boolean {
  // No preferences yet (new user) - can show after delay
  if (!preferences) return true

  // Already completed installation
  if (preferences.iosInstallWizardCompleted) return false

  // Check permanent opt-out with 30-day reappearance
  if (preferences.iosInstallWizardPermanentlyDismissed) {
    const lastDismissed = preferences.iosInstallWizardLastDismissed
    if (lastDismissed) {
      const daysSinceDismissed = (Date.now() - new Date(lastDismissed).getTime()) / (1000 * 60 * 60 * 24)
      // Reappear after 30 days
      return daysSinceDismissed > 30
    }
    // Permanently dismissed but no timestamp (shouldn't happen, but allow showing)
    return false
  }

  // Check cooldown period based on dismissal count
  const lastDismissed = preferences.iosInstallWizardLastDismissed
  if (lastDismissed) {
    const cooldownDays = getWizardCooldownDays(preferences.iosInstallWizardDismissCount)
    const daysSinceDismissed = (Date.now() - new Date(lastDismissed).getTime()) / (1000 * 60 * 60 * 24)
    return daysSinceDismissed >= cooldownDays
  }

  // Never dismissed before - can show
  return true
}

/**
 * Check if wizard can be shown now (checks localStorage for immediate feedback)
 */
export function canShowWizardNow(): boolean {
  if (typeof window === "undefined") return false

  // Check permanent opt-out in localStorage
  const permanentOptOut = localStorage.getItem(WIZARD_PERMANENT_OPT_OUT_KEY)
  if (permanentOptOut === "true") {
    // Check if 30 days have passed
    const dismissedUntil = localStorage.getItem(WIZARD_DISMISSED_UNTIL_KEY)
    if (dismissedUntil) {
      const dismissedDate = new Date(dismissedUntil)
      const daysSince = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24)
      if (daysSince < 30) {
        return false
      }
      // 30 days passed, reset opt-out
      localStorage.removeItem(WIZARD_PERMANENT_OPT_OUT_KEY)
    }
  }

  // Check cooldown in localStorage
  const dismissedUntil = localStorage.getItem(WIZARD_DISMISSED_UNTIL_KEY)
  if (dismissedUntil) {
    const dismissedDate = new Date(dismissedUntil)
    return Date.now() >= dismissedDate.getTime()
  }

  return true
}

/**
 * Track wizard dismissal in localStorage and return data for DB sync
 */
export function trackWizardDismissal(currentDismissCount: number): {
  dismissCount: number
  dismissedUntil: Date
  isPermanentOptOut: boolean
} {
  const newDismissCount = currentDismissCount + 1
  const cooldownDays = getWizardCooldownDays(newDismissCount)
  const dismissedUntil = new Date(Date.now() + cooldownDays * 24 * 60 * 60 * 1000)

  // Check if this is the 4th+ dismissal (soft permanent opt-out)
  const isPermanentOptOut = newDismissCount >= 4

  if (typeof window !== "undefined") {
    localStorage.setItem(WIZARD_DISMISSED_UNTIL_KEY, dismissedUntil.toISOString())
    if (isPermanentOptOut) {
      localStorage.setItem(WIZARD_PERMANENT_OPT_OUT_KEY, "true")
    }
  }

  return {
    dismissCount: newDismissCount,
    dismissedUntil,
    isPermanentOptOut,
  }
}

/**
 * Track wizard completion (user successfully installed PWA)
 */
export function trackWizardCompletion(): void {
  if (typeof window !== "undefined") {
    // Clear all wizard-related localStorage
    localStorage.removeItem(WIZARD_DISMISSED_UNTIL_KEY)
    localStorage.removeItem(WIZARD_PERMANENT_OPT_OUT_KEY)
  }
}

/**
 * Check if user has permanently opted out (localStorage check)
 */
export function isPermanentlyDismissed(): boolean {
  if (typeof window === "undefined") return false

  const permanentOptOut = localStorage.getItem(WIZARD_PERMANENT_OPT_OUT_KEY)
  if (permanentOptOut !== "true") return false

  // Check if 30 days have passed since dismissal
  const dismissedUntil = localStorage.getItem(WIZARD_DISMISSED_UNTIL_KEY)
  if (dismissedUntil) {
    const dismissedDate = new Date(dismissedUntil)
    const daysSince = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24)

    if (daysSince >= 30) {
      // Reset after 30 days
      localStorage.removeItem(WIZARD_PERMANENT_OPT_OUT_KEY)
      return false
    }
  }

  return true
}

/**
 * Check if wizard should reappear after opt-out (30-day check)
 */
export function shouldReappearAfterOptOut(lastDismissed: Date | null): boolean {
  if (!lastDismissed) return true

  const daysSinceDismissed = (Date.now() - new Date(lastDismissed).getTime()) / (1000 * 60 * 60 * 24)
  return daysSinceDismissed > 30
}

/**
 * Reset wizard state (for Settings toggle)
 */
export function resetWizardState(): void {
  if (typeof window !== "undefined") {
    localStorage.removeItem(WIZARD_DISMISSED_UNTIL_KEY)
    localStorage.removeItem(WIZARD_PERMANENT_OPT_OUT_KEY)
  }
}

/**
 * Get user-friendly message for next wizard appearance
 */
export function getNextAppearanceMessage(dismissCount: number, lastDismissed: Date | null): string {
  if (!lastDismissed) return "Průvodce se zobrazí brzy"

  const cooldownDays = getWizardCooldownDays(dismissCount)
  const nextAppearance = new Date(new Date(lastDismissed).getTime() + cooldownDays * 24 * 60 * 60 * 1000)

  const daysRemaining = Math.ceil((nextAppearance.getTime() - Date.now()) / (1000 * 60 * 60 * 24))

  if (daysRemaining <= 0) return "Průvodce se zobrazí brzy"
  if (daysRemaining === 1) return "Průvodce se zobrazí zítra"
  if (daysRemaining <= 7) return `Průvodce se zobrazí za ${daysRemaining} dní`
  if (daysRemaining <= 14) return "Průvodce se zobrazí příští týden"
  if (daysRemaining <= 30) return "Průvodce se zobrazí za 2 týdny"

  return "Průvodce se zobrazí za měsíc"
}
